import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import SearchFilterBar, { filterRecords } from '@/components/SearchFilterBar';
import { ClipboardText } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_LABELS = {
  user_created: 'User created',
  project_code_created: 'Project code created',
  requisition_created: 'Requisition created',
  quotation_added: 'Quotation added',
  vendor_approved: 'Vendor approved',
  po_created: 'PO created',
  po_released: 'PO released',
};

const ENTITY_OPTIONS = [
  { value: 'requisition', label: 'Requisitions' },
  { value: 'quotation', label: 'Quotations' },
  { value: 'purchase_order', label: 'Purchase Orders' },
  { value: 'project_code', label: 'Project Codes' },
  { value: 'user', label: 'Users' },
];

const AuditTrailPage = () => {
  const { getAuthHeader } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [entityType, setEntityType] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (entityType && entityType !== 'all') params.append('entity_type', entityType);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const url = `${API}/audit-logs${params.toString() ? '?' + params.toString() : ''}`;
      const res = await axios.get(url, { headers: getAuthHeader() });
      setLogs(res.data || []);
    } catch (e) {
      toast.error('Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [entityType, startDate, endDate]);

  const filtered = useMemo(
    () =>
      filterRecords(logs, {
        keyword,
        fields: ['entity_label', 'action', 'entity_type'],
        dateField: 'timestamp',
      }),
    [logs, keyword]
  );

  const clearFilters = () => {
    setKeyword('');
    setEntityType('all');
    setStartDate('');
    setEndDate('');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tighter text-text-primary">
            My Audit Trail
          </h1>
          <p className="mt-2 text-text-secondary">
            A record of every action you have taken in this system.
          </p>
        </div>

        <SearchFilterBar
          keyword={keyword}
          onKeywordChange={setKeyword}
          statusValue={entityType}
          onStatusChange={setEntityType}
          statusOptions={ENTITY_OPTIONS}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onClear={clearFilters}
          placeholder="Search action or entity…"
          testIdPrefix="audit-filter"
        />

        <div className="bg-white border border-border-default rounded">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface border-b border-border-default">
                <tr>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">When</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Action</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Target</th>
                  <th className="text-left p-4 text-xs font-bold uppercase tracking-[0.2em] text-text-secondary">Details</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="4" className="p-6 text-center text-text-tertiary">Loading…</td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan="4" className="p-8 text-center text-text-tertiary">
                      <ClipboardText size={32} weight="duotone" className="mx-auto mb-2 text-text-tertiary" />
                      No actions yet. Actions you perform will appear here.
                    </td>
                  </tr>
                )}
                {!loading &&
                  filtered.map((log) => {
                    const detailsShort = Object.entries(log.details || {})
                      .filter(([k]) => k !== 'source')
                      .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${typeof v === 'number' ? (k.includes('price') || k.includes('amount') ? `₹${Number(v).toFixed(2)}` : v) : v}`)
                      .slice(0, 3)
                      .join(' · ');
                    return (
                      <tr key={log.id} className="border-b border-border-default hover:bg-surface transition-colors" data-testid={`audit-row-${log.id}`}>
                        <td className="p-4 text-sm text-text-secondary whitespace-nowrap">
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="p-4">
                          <span className="text-sm font-medium text-text-primary">
                            {ACTION_LABELS[log.action] || log.action.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="p-4 text-text-primary">
                          <div className="text-sm">{log.entity_label || '—'}</div>
                          <div className="text-xs text-text-tertiary">{log.entity_type}</div>
                        </td>
                        <td className="p-4 text-sm text-text-secondary">{detailsShort || '—'}</td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AuditTrailPage;
